/**
 * Onboarding Success Page
 * Displayed after successful onboarding completion
 */

import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

export default function OnboardingSuccess() {
    const navigate = useNavigate();

    // Get user_id from localStorage
    const userId = localStorage.getItem('user_id');

    useEffect(() => {
        // If no user_id, redirect to onboarding
        if (!userId) {
            navigate('/onboarding');
        }
    }, [userId, navigate]);

    return (
        <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50 flex items-center justify-center p-4">
            <div className="max-w-2xl w-full">
                <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 text-center">
                    {/* Success Icon */}
                    <div className="w-24 h-24 flex items-center justify-center bg-emerald-100 rounded-full mx-auto mb-6 animate-bounce">
                        <i className="ri-checkbox-circle-line text-emerald-600 text-5xl"></i>
                    </div>

                    {/* Success Message */}
                    <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                        Onboarding Completed Successfully! 🎉
                    </h1>
                    <p className="text-lg text-gray-600 mb-8">
                        Your login credentials have been sent to the email address used during registration.
                    </p>

                    {/* Next Steps */}
                    <div className="bg-gray-50 rounded-lg p-6 mb-8 text-left">
                        <h2 className="font-bold text-gray-900 mb-4 text-center">What Happens Next?</h2>
                        <ul className="space-y-3">
                            <li className="flex items-start">
                                <i className="ri-mail-line text-emerald-600 mr-3 mt-1 flex-shrink-0"></i>
                                <span className="text-gray-700">
                                    <strong>Check your registered email</strong> (used during registration) for your login password
                                </span>
                            </li>
                            <li className="flex items-start">
                                <i className="ri-login-box-line text-emerald-600 mr-3 mt-1 flex-shrink-0"></i>
                                <span className="text-gray-700">
                                    <strong>Login</strong> with your email and the password sent to you
                                </span>
                            </li>
                            <li className="flex items-start">
                                <i className="ri-dashboard-line text-emerald-600 mr-3 mt-1 flex-shrink-0"></i>
                                <span className="text-gray-700">
                                    Access your <strong>SEO Strategy Dashboard</strong> with detailed insights
                                </span>
                            </li>
                            <li className="flex items-start">
                                <i className="ri-line-chart-line text-emerald-600 mr-3 mt-1 flex-shrink-0"></i>
                                <span className="text-gray-700">
                                    Start tracking your brand's business growth through SEO
                                </span>
                            </li>
                        </ul>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <button
                            onClick={() => navigate('/?login=true')}
                            className="px-8 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 font-semibold transition-colors cursor-pointer"
                        >
                            Go to Login <i className="ri-arrow-right-line ml-2"></i>
                        </button>
                        <button
                            onClick={() => navigate('/')}
                            className="px-8 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-semibold transition-colors"
                        >
                            Back to Home
                        </button>
                    </div>

                    {/* Support */}
                    <div className="mt-8 pt-6 border-t border-gray-200">
                        <p className="text-sm text-gray-600">
                            Need Help? Contact us at{' '}
                            <a href="mailto:support@quantyraanalytics.com" className="text-emerald-600 hover:text-emerald-700 font-semibold">
                                support@quantyraanalytics.com
                            </a>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}
